import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sun, Moon, LogIn, Truck, BadgeDollarSign, PackageCheck, Globe2, MapPin, Clock, Phone, Mail, CreditCard, Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../App";
import { useLang } from "../context/LanguageContext";
import { LangToggle } from "../components/LangToggle";
import { api, errDetail } from "../lib/api";
import { toast } from "sonner";

const HERO_IMGS = [
  "https://images.unsplash.com/photo-1617811449482-31093c8cee16?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1635353059173-461b9c459f2a?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1558505780-1e584fab3ede?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1749114764529-0b57906bc6a3?auto=format&fit=crop&w=1600&q=80",
];
const LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_cloth-commerce-pro-2/artifacts/tdcy26d1_lasvegas-logo-tras.png";
const WOMPI_URL = "https://checkout.wompi.pa/l/VPOS_Mk0jwx";

const BRANDS = [
  "leopoldo", "piel", "avis-club", "leonor", "platini",
  "future-comfort", "avis-sport", "marshal", "platini-2", "la-condessa",
];

const CATEGORIES = [
  { es: "Toallas", en: "Towels", img: "https://images.unsplash.com/photo-1728034261564-18930dcb2c8e?auto=format&fit=crop&w=600&q=80" },
  { es: "Sábanas", en: "Bed Sheets", img: "https://images.unsplash.com/photo-1601276174812-63280a55656e?auto=format&fit=crop&w=600&q=80" },
  { es: "Cortinas", en: "Curtains", img: "https://images.pexels.com/photos/14281158/pexels-photo-14281158.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { es: "Medias", en: "Socks", img: "https://images.unsplash.com/photo-1585499583264-491df5142e83?auto=format&fit=crop&w=600&q=80" },
  { es: "Pijamas", en: "Sleepwear", img: "https://images.unsplash.com/photo-1766056278825-55168658f120?auto=format&fit=crop&w=600&q=80" },
  { es: "Ropa Interior", en: "Underwear", img: "https://images.unsplash.com/photo-1604176354204-9268737828e4?auto=format&fit=crop&w=600&q=80" },
];

const T = {
  es: {
    login: "Iniciar Sesión", heroTitle: "Mayoristas de textiles y ropa",
    heroSub: "Más de 40 años en la Zona Libre de Colón, Panamá. Los mejores precios al por mayor para tu negocio.",
    heroCta: "Acceder al catálogo", catsTitle: "Algunos de nuestros productos",
    catsSub: "Amplia variedad en textiles para el hogar y prendas de vestir.",
    benefitsTitle: "¿Por qué comprar al por mayor con nosotros?",
    b1t: "Precios competitivos", b1d: "Precios por docena directos de importación, sin intermediarios.",
    b2t: "Envíos internacionales", b2d: "Exportamos a toda la región desde la zona franca más grande del hemisferio.",
    b3t: "Stock garantizado", b3d: "Amplio inventario y reposición constante de todas las categorías.",
    b4t: "40+ años de experiencia", b4d: "Trayectoria y relaciones comerciales sólidas a nivel global.",
    aboutTitle: "Acerca de nosotros",
    about: "Las Vegas Nevadas se ha consolidado como líder en importación y exportación, ofreciendo soluciones integrales de comercio internacional con los precios más competitivos del mercado, manteniendo los más altos estándares de calidad y servicio.",
    contactTitle: "Contáctanos", address: "Dirección", addressVal: "Las Vegas, Zona Libre de Colón, Panamá",
    hours: "Horario", hoursVal: "Lunes a Viernes, 8:00 AM - 5:00 PM", contact: "Contacto", directions: "Cómo llegar",
    pay: "Pagar en línea",
    brandsTitle: "Nuestras marcas", brandsSub: "Marcas propias y exclusivas que distribuimos.",
    formTitle: "Contáctanos", fName: "Nombre", fEmail: "Email", fPhone: "Teléfono", fMsg: "Mensaje",
    fRequired: "Requerido", fPhoneHelp: "Un número de teléfono nos ayuda a contactarlo más rápido.",
    fMsgHelp: "Déjanos tu pregunta o solicitud en detalle.",
    fSend: "Enviar", fSending: "Enviando...", fOk: "¡Mensaje enviado! Te contactaremos pronto.",
    fNamePh: "Juan Pérez", fEmailPh: "ejemplo@correo.com", fPhonePh: "+507 6000-0000", fMsgPh: "Describe tu consulta o solicitud en detalle",
    fLegal: "Al enviar este formulario, acepta nuestra política de tratamiento de datos personales.",
    fHuman: "No soy un robot", fCaptcha: "Verificación: ¿cuánto es", fCaptchaPh: "Respuesta", fHumanErr: "Confirma que no eres un robot",
    rights: "Todos los derechos reservados.",
  },
  en: {
    login: "Sign In", heroTitle: "Textile & apparel wholesalers",
    heroSub: "Over 40 years in the Colón Free Zone, Panama. The best wholesale prices for your business.",
    heroCta: "Access the catalog", catsTitle: "Some of our products",
    catsSub: "Wide variety of home textiles and apparel.",
    benefitsTitle: "Why buy wholesale with us?",
    b1t: "Competitive prices", b1d: "Per-dozen prices straight from import, no middlemen.",
    b2t: "International shipping", b2d: "We export across the region from the largest free zone in the hemisphere.",
    b3t: "Guaranteed stock", b3d: "Large inventory and constant restocking across all categories.",
    b4t: "40+ years of experience", b4d: "Proven track record and solid global business relationships.",
    aboutTitle: "About us",
    about: "Las Vegas Nevadas is a leader in import and export, offering integrated international trade solutions at the most competitive prices, upholding the highest standards of quality and service.",
    contactTitle: "Contact us", address: "Address", addressVal: "Las Vegas, Colón Free Zone, Panama",
    hours: "Hours", hoursVal: "Monday to Friday, 8:00 AM - 5:00 PM", contact: "Contact", directions: "Get directions",
    pay: "Pay online",
    brandsTitle: "Our brands", brandsSub: "Our own and exclusive brands that we distribute.",
    formTitle: "Contact us", fName: "Name", fEmail: "Email", fPhone: "Phone", fMsg: "Message",
    fRequired: "Required", fPhoneHelp: "A phone number helps us reach you faster.",
    fMsgHelp: "Leave your question or request in detail.",
    fSend: "Send", fSending: "Sending...", fOk: "Message sent! We'll contact you soon.",
    fNamePh: "John Doe", fEmailPh: "example@mail.com", fPhonePh: "+1 222-333-4444", fMsgPh: "Describe your question or request in detail",
    fLegal: "By submitting this form, you accept our personal data processing policy.",
    fHuman: "I'm not a robot", fCaptcha: "Verification: what is", fCaptchaPh: "Answer", fHumanErr: "Please confirm you are not a robot",
    rights: "All rights reserved.",
  },
};

export default function LandingPage() {
  const { settings } = useAuth();
  const theme = useTheme();
  const { lang } = useLang();
  const L = T[lang] || T.es;
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMGS.length), 5000);
    return () => clearInterval(id);
  }, []);

  const benefits = [
    { icon: BadgeDollarSign, t: L.b1t, d: L.b1d },
    { icon: Truck, t: L.b2t, d: L.b2d },
    { icon: PackageCheck, t: L.b3t, d: L.b3d },
    { icon: Globe2, t: L.b4t, d: L.b4d },
  ];

  const [cform, setCform] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [human, setHuman] = useState(false);
  const [captcha, setCaptcha] = useState(() => ({ a: 1 + Math.floor(Math.random() * 9), b: 1 + Math.floor(Math.random() * 9) }));
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const resetCaptcha = () => { setCaptcha({ a: 1 + Math.floor(Math.random() * 9), b: 1 + Math.floor(Math.random() * 9) }); setCaptchaAnswer(""); setHuman(false); };
  const submitContact = async (e) => {
    e.preventDefault();
    if (!human) { toast.error(L.fHumanErr); return; }
    setSending(true);
    try {
      await api.post("/contact", { ...cform, human, captcha_a: captcha.a, captcha_b: captcha.b, captcha_answer: captchaAnswer, website: honeypot });
      toast.success(L.fOk);
      setCform({ name: "", email: "", phone: "", message: "" });
      resetCaptcha();
    } catch (err) {
      toast.error(errDetail(err));
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 dark:bg-gray-900/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {settings.logo_url && <img src={LOGO} alt="Las Vegas Nevadas" className="h-9 w-auto object-contain" />}
            <span className="font-heading font-bold text-lg truncate">{settings.platform_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <LangToggle />
            <button onClick={theme.toggle} data-testid="landing-theme-toggle"
              className="p-2 rounded-full hover:bg-muted transition-colors" aria-label="theme">
              {theme.dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link to="/login" data-testid="landing-login-button"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity">
              <LogIn size={16} /> {L.login}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        {HERO_IMGS.map((src, i) => (
          <img key={src} src={src} alt="" aria-hidden={i !== heroIdx}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
            style={{ opacity: i === heroIdx ? 1 : 0 }} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/80 to-slate-950/50" />
        <div className="relative max-w-7xl mx-auto px-4 py-24 sm:py-32 lg:py-40">
          <div className="max-w-2xl space-y-6 fade-up">
            <div className="bg-white rounded-2xl shadow-xl p-4 w-fit"><img src={LOGO} alt="Las Vegas Nevadas" className="h-20 sm:h-28 w-auto object-contain" /></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-white leading-tight drop-shadow-lg">{L.heroTitle}</h1>
            <p className="text-slate-200 text-base md:text-lg max-w-xl">{L.heroSub}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/login" data-testid="landing-hero-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-amber-500 text-slate-950 font-bold hover:bg-amber-400 transition-colors">
                <LogIn size={18} /> {L.heroCta}
              </Link>
              <a href={WOMPI_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-payment-link"
                className="group inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 text-white font-extrabold shadow-lg shadow-emerald-500/40 ring-2 ring-emerald-300/50 hover:from-emerald-400 hover:to-green-500 hover:scale-105 hover:shadow-emerald-400/60 transition-all duration-300 animate-pulse-slow">
                <CreditCard size={20} className="group-hover:rotate-6 transition-transform" />
                <span>Pagar en línea <span className="opacity-70 font-semibold">· Pay online</span></span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20" data-testid="landing-categories">
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
          <h2 className="text-2xl sm:text-3xl font-bold font-heading">{L.catsTitle}</h2>
          <p className="text-muted-foreground text-sm md:text-base">{L.catsSub}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {CATEGORIES.map((c) => (
            <div key={c.es} className="group relative aspect-square rounded-2xl overflow-hidden border border-border" data-testid={`landing-category-${c.es}`}>
              <img src={c.img} alt={c[lang] || c.es} loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 to-transparent" />
              <span className="absolute bottom-2 left-2 right-2 text-white font-semibold text-sm drop-shadow">{c[lang] || c.es}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-muted/40 border-y border-border" data-testid="landing-benefits">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-bold font-heading text-center mb-10">{L.benefitsTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b) => (
              <div key={b.t} className="bg-card border border-border rounded-2xl p-6 space-y-3 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center"><b.icon size={24} /></div>
                <h3 className="font-semibold text-lg">{b.t}</h3>
                <p className="text-sm text-muted-foreground">{b.d}</p>
              </div>
            ))}
          </div>
          <div className="max-w-3xl mx-auto text-center mt-14 space-y-3">
            <h3 className="text-xl font-bold font-heading">{L.aboutTitle}</h3>
            <p className="text-sm md:text-base text-muted-foreground">{L.about}</p>
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20" data-testid="landing-brands">
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
          <h2 className="text-2xl sm:text-3xl font-bold font-heading">{L.brandsTitle}</h2>
          <p className="text-muted-foreground text-sm md:text-base">{L.brandsSub}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
          {BRANDS.map((b) => (
            <div key={b} data-testid={`landing-brand-${b}`}
              className="group flex items-center justify-center bg-white rounded-2xl border border-border p-6 h-28 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
              <img src={`/brands/${b}.png?v=2`} alt={b} loading="lazy"
                className="max-h-full max-w-full object-contain" />
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form */}
      <section className="border-t border-border bg-muted/30" data-testid="landing-contact-form">
        <div className="max-w-2xl mx-auto px-4 py-16 sm:py-20">
          <h2 className="text-3xl sm:text-4xl font-bold font-heading text-center mb-10">{L.formTitle}</h2>
          <form onSubmit={submitContact} className="space-y-5" data-testid="contact-form">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium">{L.fName}</label>
                <span className="text-xs text-muted-foreground">{L.fRequired}</span>
              </div>
              <input required value={cform.name} onChange={(e) => setCform({ ...cform, name: e.target.value })}
                data-testid="contact-name" placeholder={L.fNamePh}
                className="w-full px-4 py-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium">{L.fEmail}</label>
                <span className="text-xs text-muted-foreground">{L.fRequired}</span>
              </div>
              <input required type="email" value={cform.email} onChange={(e) => setCform({ ...cform, email: e.target.value })}
                data-testid="contact-email" placeholder={L.fEmailPh}
                className="w-full px-4 py-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">{L.fPhone}</label>
              <input value={cform.phone} onChange={(e) => setCform({ ...cform, phone: e.target.value })}
                data-testid="contact-phone" placeholder={L.fPhonePh}
                className="w-full px-4 py-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              <p className="text-xs text-muted-foreground mt-1.5">{L.fPhoneHelp}</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium">{L.fMsg}</label>
                <span className="text-xs text-muted-foreground">{L.fRequired}</span>
              </div>
              <textarea required rows={5} value={cform.message} onChange={(e) => setCform({ ...cform, message: e.target.value })}
                data-testid="contact-message" placeholder={L.fMsgPh}
                className="w-full px-4 py-3 rounded-xl border border-input bg-background text-sm resize-y focus:outline-none focus:ring-2 focus:ring-amber-600" />
              <p className="text-xs text-muted-foreground mt-1.5">{L.fMsgHelp}</p>
            </div>
            <input type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off"
              className="hidden" aria-hidden="true" data-testid="contact-honeypot" />
            <div className="rounded-xl border border-input bg-background p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="contact-human-check">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={human} onChange={(e) => setHuman(e.target.checked)} data-testid="contact-human-checkbox"
                  className="h-5 w-5 rounded border-input accent-amber-500" />
                <span className="text-sm font-medium">{L.fHuman}</span>
              </label>
              <div className="flex items-center gap-2 sm:ml-auto">
                <span className="text-sm text-muted-foreground" data-testid="contact-captcha-question">{L.fCaptcha} {captcha.a} + {captcha.b}?</span>
                <input type="number" required value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)} inputMode="numeric"
                  data-testid="contact-captcha-answer" placeholder={L.fCaptchaPh}
                  className="w-24 px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
              <button type="submit" disabled={sending} data-testid="contact-submit"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-amber-500 text-slate-950 font-bold hover:bg-amber-400 disabled:opacity-60 transition-colors">
                <Send size={17} /> {sending ? L.fSending : L.fSend}
              </button>
              <p className="text-xs text-muted-foreground flex-1">{L.fLegal}</p>
            </div>
          </form>
        </div>
      </section>

      {/* Contact / Location cards */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20" data-testid="landing-contact">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-card border border-border rounded-2xl p-6 space-y-2">
            <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center"><MapPin size={22} /></div>
            <h3 className="font-semibold">{L.address}</h3>
            <p className="text-sm text-muted-foreground">{L.addressVal}</p>
            <a href="https://www.google.com/maps/dir/?api=1&destination=Las+Vegas%2C+Zona+Libre+de+Col%C3%B3n" target="_blank" rel="noreferrer"
              className="text-sm text-amber-600 font-medium hover:underline" data-testid="landing-directions">{L.directions} →</a>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6 space-y-2">
            <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center"><Clock size={22} /></div>
            <h3 className="font-semibold">{L.hours}</h3>
            <p className="text-sm text-muted-foreground">{L.hoursVal}</p>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6 space-y-2">
            <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center"><Phone size={22} /></div>
            <h3 className="font-semibold">{L.contact}</h3>
            <a href="tel:+5074417746" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-amber-600"><Phone size={14} /> +507 441 7746</a>
            <a href="mailto:info@lasvegaszl.com" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-amber-600"><Mail size={14} /> info@lasvegaszl.com</a>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} {settings.platform_name}. {L.rights}
      </footer>
    </div>
  );
}
